import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import NotificationBell from '../components/NotificationBell';
import { 
  ArrowLeft,
  Loader2,
  TrendingUp,
  Building2,
  Briefcase,
  Target,
  Sparkles,
  ChevronRight,
  Clock,
  Award,
  Lightbulb,
  ArrowUpRight,
  CheckCircle,
  Circle,
  HelpCircle,
  BookOpen,
  RefreshCw,
  Zap,
  MapPin,
  GraduationCap,
  Check,
  Shuffle,
  DoorOpen,
  Upload,
  Star,
  Rocket,
  User
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Persona configurations
const PERSONA_CONFIG = {
  new_grad: {
    label: "New Graduate",
    description: "Starting your pharma career journey",
    icon: GraduationCap,
    color: "blue",
    bgGradient: "from-blue-50 to-indigo-50",
    borderColor: "border-blue-200",
    badgeClass: "bg-blue-100 text-blue-800"
  },
  career_changer: {
    label: "Career Changer", 
    description: "Transitioning into life sciences",
    icon: Shuffle,
    color: "purple",
    bgGradient: "from-purple-50 to-pink-50",
    borderColor: "border-purple-200",
    badgeClass: "bg-purple-100 text-purple-800"
  },
  breaking_in: {
    label: "Breaking In",
    description: "Building your pharma foundation",
    icon: DoorOpen,
    color: "amber",
    bgGradient: "from-amber-50 to-orange-50",
    borderColor: "border-amber-200",
    badgeClass: "bg-amber-100 text-amber-800"
  },
  pharma_veteran: {
    label: "Pharma Veteran",
    description: "Established life sciences professional",
    icon: Award,
    color: "green",
    bgGradient: "from-lime-50 to-emerald-50",
    borderColor: "border-lime-200",
    badgeClass: "bg-lime-100 text-lime-800"
  }
};

// Seniority levels in typical life sciences career ladder
const SENIORITY_LADDER = [
  { level: 'entry', label: 'Entry Level', titles: ['analyst', 'associate', 'coordinator', 'assistant', 'specialist'] },
  { level: 'mid', label: 'Mid Level', titles: ['senior', 'lead', 'manager', 'scientist'] },
  { level: 'senior', label: 'Senior Level', titles: ['principal', 'senior manager', 'associate director'] },
  { level: 'director', label: 'Director', titles: ['director'] },
  { level: 'senior_director', label: 'Senior Director', titles: ['senior director', 'sr director', 'sr. director'] },
  { level: 'vp', label: 'VP / Head', titles: ['vice president', 'vp', 'head of', 'head,'] },
  { level: 'svp', label: 'SVP / Executive', titles: ['senior vice president', 'svp', 'executive director'] },
  { level: 'c_suite', label: 'C-Suite', titles: ['ceo', 'cfo', 'cmo', 'coo', 'cto', 'chief executive', 'chief medical', 'chief operating', 'chief financial', 'chief technology', 'chief scientific', 'chief commercial'] }
];

// Helper to check if title contains keyword as a whole word/phrase
const titleMatchesKeyword = (title, keyword) => {
  // Create regex with word boundaries for whole-word matching
  const escapedKeyword = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(`\\b${escapedKeyword}\\b`, 'i');
  return regex.test(title);
};

// Infer seniority level from job title
const inferSeniorityLevel = (title) => {
  if (!title) return 'entry';
  const lowerTitle = title.toLowerCase();
  
  // Check from top to bottom (most senior first)
  for (let i = SENIORITY_LADDER.length - 1; i >= 0; i--) {
    const level = SENIORITY_LADDER[i];
    if (level.titles.some(t => titleMatchesKeyword(lowerTitle, t))) {
      return level.level;
    }
  }
  return 'entry'; // Default to entry-level for unknown titles
};

// Get seniority index for positioning
const getSeniorityIndex = (level) => {
  return SENIORITY_LADDER.findIndex(l => l.level === level);
};

// Industry colors
const INDUSTRY_COLORS = {
  'big pharma': { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-200' },
  'biotech': { bg: 'bg-purple-100', text: 'text-purple-800', border: 'border-purple-200' },
  'cro': { bg: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-200' },
  'medtech': { bg: 'bg-teal-100', text: 'text-teal-800', border: 'border-teal-200' },
  'consulting': { bg: 'bg-slate-100', text: 'text-slate-800', border: 'border-slate-200' },
  'tech': { bg: 'bg-cyan-100', text: 'text-cyan-800', border: 'border-cyan-200' },
  'finance': { bg: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-200' },
  'default': { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' }
};

const getIndustryColor = (industry) => {
  if (!industry) return INDUSTRY_COLORS.default;
  const lower = industry.toLowerCase();
  return INDUSTRY_COLORS[lower] || INDUSTRY_COLORS.default;
};

// Persona Banner Component
const PersonaBanner = ({ persona }) => {
  const config = PERSONA_CONFIG[persona] || PERSONA_CONFIG.pharma_veteran;
  const IconComponent = config.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-gradient-to-r ${config.bgGradient} border ${config.borderColor} rounded-xl p-4 mb-6`}
    >
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg ${config.badgeClass} flex items-center justify-center`}>
          <IconComponent className="h-5 w-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-slate-900">{config.label}</span>
            <Badge variant="outline" className={config.badgeClass}>
              Your Profile Type
            </Badge>
          </div>
          <p className="text-sm text-slate-600">{config.description}</p>
        </div>
      </div>
    </motion.div>
  );
};

// New Grad Starting Point Component (replaces timeline for new grads)
const NewGradStartingPoint = ({ profile, hasResumeUploaded, needsResumeAnalysis, onAnalyzeResume, isAnalyzing }) => {
  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Rocket className="h-5 w-5 text-blue-600" />
          Starting Your Journey
        </CardTitle>
        <CardDescription>
          You&apos;re at the beginning of an exciting career in life sciences
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Entry level visualization */}
          <div className="relative">
            <div className="flex items-center gap-4 p-4 bg-white rounded-xl border border-blue-100">
              <div className="h-12 w-12 rounded-full bg-blue-500 flex items-center justify-center text-white">
                <Star className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs text-blue-600 font-medium uppercase tracking-wide">Your Starting Point</p>
                <p className="text-lg font-semibold text-slate-900">Entry Level</p>
                <p className="text-sm text-slate-600">Ready to launch into pharma/biotech</p>
              </div>
            </div>
          </div>

          {/* Tips for new grads */}
          <div className="bg-white/70 rounded-lg p-4 border border-blue-100">
            <p className="text-sm font-medium text-slate-700 mb-3">Quick tips to get started:</p>
            <ul className="space-y-2">
              {[
                hasResumeUploaded 
                  ? "Your resume is uploaded! Click below to analyze it for insights"
                  : "Upload your resume to unlock personalized pathway recommendations",
                "Look for Associate, Coordinator, or Analyst roles",
                "Consider GCP certification to stand out",
                "Internships and contract roles are great entry points"
              ].map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>

          {needsResumeAnalysis ? (
            <Button 
              onClick={onAnalyzeResume}
              disabled={isAnalyzing}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white gap-2"
            >
              {isAnalyzing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Analyzing Resume...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Analyze Resume for Insights
                </>
              )}
            </Button>
          ) : hasResumeUploaded ? (
            <Link to="/profile">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2">
                <User className="h-4 w-4" />
                View Your Profile
              </Button>
            </Link>
          ) : (
            <Link to="/resumes">
              <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2">
                <Upload className="h-4 w-4" />
                Upload Resume for Personalized Insights
              </Button>
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Career Changer Bridge Component
const CareerChangerBridge = ({ insights, profile }) => {
  const industries = insights?.industry_types || [];
  const skills = insights?.top_skills || [];
  const currentTitle = insights?.last_title || profile?.current_title;
  
  // Identify pharma vs non-pharma industries
  const pharmaIndustries = ['big pharma', 'biotech', 'cro', 'medtech', 'diagnostics'];
  const hasPharmaExp = industries.some(i => pharmaIndustries.includes(i.toLowerCase()));
  const nonPharmaIndustries = industries.filter(i => !pharmaIndustries.includes(i.toLowerCase()));

  return (
    <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shuffle className="h-5 w-5 text-purple-600" />
          Your Career Bridge
        </CardTitle>
        <CardDescription>
          Transitioning your experience into life sciences
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Previous experience */}
          <div className="flex items-center gap-4">
            <div className="flex-1 p-3 bg-white rounded-lg border border-slate-200">
              <p className="text-xs text-slate-500 uppercase tracking-wide mb-1">Your Background</p>
              <p className="font-medium text-slate-900">{currentTitle || 'Professional'}</p>
              {nonPharmaIndustries.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {nonPharmaIndustries.map((ind, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs capitalize">
                      {ind}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex flex-col items-center">
              <ArrowUpRight className="h-6 w-6 text-purple-500" />
              <span className="text-xs text-purple-600 font-medium">Bridge</span>
            </div>
            
            <div className="flex-1 p-3 bg-purple-100 rounded-lg border border-purple-200">
              <p className="text-xs text-purple-600 uppercase tracking-wide mb-1">Target</p>
              <p className="font-medium text-slate-900">Life Sciences / Pharma</p>
              <p className="text-sm text-slate-600">Biotech, Pharma, MedTech</p>
            </div>
          </div>

          {/* Transferable skills highlight */}
          {skills.length > 0 && (
            <div className="bg-white/70 rounded-lg p-4 border border-purple-100">
              <p className="text-sm font-medium text-slate-700 mb-2">Your transferable skills:</p>
              <div className="flex flex-wrap gap-2">
                {skills.slice(0, 5).map((skill, idx) => (
                  <Badge key={idx} className="bg-purple-100 text-purple-800 border-purple-200">
                    {skill}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {hasPharmaExp && (
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm text-green-800 flex items-center gap-2">
                <CheckCircle className="h-4 w-4" />
                You already have some pharma experience — you&apos;re on your way!
              </p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// Career Timeline Component
const CareerTimeline = ({ insights, profile, onAnalyzeResume, isAnalyzing, hasResumeUploaded }) => {
  const companies = insights?.company_history || [];
  const careerSpan = insights?.career_span_years || 0;
  const industries = insights?.industry_types || [];
  const currentTitle = insights?.last_title || profile?.current_title;
  const currentLevel = inferSeniorityLevel(currentTitle);

  if (companies.length === 0) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50/50">
        <CardContent className="py-8 text-center">
          <Clock className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-2">No career history available</p>
          {hasResumeUploaded ? (
            <>
              <p className="text-sm text-slate-400 mb-4">Your resume needs to be analyzed to show your timeline</p>
              <Button 
                onClick={onAnalyzeResume}
                disabled={isAnalyzing}
                variant="outline" 
                size="sm"
                className="gap-2"
              >
                {isAnalyzing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4" />
                    Analyze Resume
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-400">Upload your resume to see your career timeline</p>
              <Link to="/resumes">
                <Button variant="outline" size="sm" className="mt-4">
                  Upload Resume
                </Button>
              </Link>
            </>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-lime-600" />
              Your Career Journey
            </CardTitle>
            <CardDescription>
              {careerSpan} years across {companies.length} organizations
            </CardDescription>
          </div>
          <div className="flex gap-2">
            {industries.slice(0, 3).map((industry, idx) => {
              const colors = getIndustryColor(industry);
              return (
                <Badge key={idx} variant="outline" className={`${colors.bg} ${colors.text} ${colors.border} capitalize`}>
                  {industry}
                </Badge>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Timeline */}
        <div className="relative">
          {/* Timeline line - gradient from lime (recent/left) to slate (older/right) */}
          <div className="absolute top-6 left-0 right-0 h-1 bg-gradient-to-r from-lime-500 via-lime-300 to-slate-200 rounded-full" />
          
          {/* Timeline nodes */}
          <div className="relative flex justify-between items-start pt-3">
            {companies.slice(0, 5).map((company, idx) => {
              const isFirst = idx === 0; // First item is most recent
              const colors = getIndustryColor(industries[idx] || industries[0]);
              
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex flex-col items-center text-center"
                  style={{ width: `${100 / Math.min(companies.length, 5)}%` }}
                >
                  {/* Node */}
                  <div className={`w-6 h-6 rounded-full border-4 ${isFirst ? 'bg-lime-500 border-lime-600' : 'bg-white border-slate-300'} relative z-10`}>
                    {isFirst && <div className="absolute inset-0 rounded-full bg-lime-400 animate-ping opacity-50" />}
                  </div>
                  
                  {/* Company name */}
                  <div className="mt-3 px-1">
                    <p className="font-medium text-sm text-slate-900 truncate max-w-[120px]" title={company}>
                      {company}
                    </p>
                    {industries[idx] && (
                      <span className={`text-xs ${colors.text} capitalize`}>
                        {industries[idx]}
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Current position callout */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-8 p-4 bg-gradient-to-r from-lime-50 to-emerald-50 rounded-xl border border-lime-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-lime-700 font-medium uppercase tracking-wide">Current Position</p>
                <p className="text-lg font-semibold text-slate-900 mt-1">{currentTitle}</p>
                <p className="text-sm text-slate-600">{companies[0]}</p>
              </div>
              <div className="text-right">
                <Badge className="bg-lime-600 text-white">
                  {SENIORITY_LADDER.find(l => l.level === currentLevel)?.label || 'Professional'}
                </Badge>
              </div>
            </div>
          </motion.div>
        </div>
      </CardContent>
    </Card>
  );
};

// Trajectory Map Component
const TrajectoryMap = ({ insights, preferences, profile }) => {
  const currentTitle = insights?.last_title || profile?.current_title;
  const currentLevel = inferSeniorityLevel(currentTitle);
  const currentIndex = getSeniorityIndex(currentLevel);
  
  // Get target from preferences
  const targetSeniority = preferences?.target_seniority_level;
  const targetIndex = targetSeniority ? getSeniorityIndex(targetSeniority) : currentIndex + 1;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-lime-600" />
          Career Trajectory
        </CardTitle>
        <CardDescription>
          Your position on the life sciences career ladder
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative py-4">
          {/* Ladder visualization */}
          <div className="space-y-2">
            {[...SENIORITY_LADDER].reverse().map((level, idx) => {
              const actualIdx = SENIORITY_LADDER.length - 1 - idx;
              const isCurrent = actualIdx === currentIndex;
              const isTarget = actualIdx === targetIndex;
              const isPast = actualIdx < currentIndex;
              const isFuture = actualIdx > currentIndex;

              return (
                <motion.div
                  key={level.level}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className={`relative flex items-center gap-4 p-3 rounded-lg transition-all ${
                    isCurrent 
                      ? 'bg-lime-100 border-2 border-lime-500' 
                      : isTarget 
                        ? 'bg-blue-50 border-2 border-blue-400 border-dashed'
                        : isPast
                          ? 'bg-slate-50 opacity-60'
                          : 'bg-white border border-slate-100'
                  }`}
                >
                  {/* Level indicator */}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isCurrent 
                      ? 'bg-lime-500 text-white' 
                      : isTarget
                        ? 'bg-blue-500 text-white'
                        : isPast 
                          ? 'bg-slate-300 text-white'
                          : 'bg-slate-100 text-slate-400'
                  }`}>
                    {isCurrent ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : isTarget ? (
                      <Target className="h-5 w-5" />
                    ) : isPast ? (
                      <Check className="h-4 w-4" />
                    ) : (
                      <Circle className="h-4 w-4" />
                    )}
                  </div>

                  {/* Level info */}
                  <div className="flex-1">
                    <p className={`font-medium ${isCurrent || isTarget ? 'text-slate-900' : 'text-slate-600'}`}>
                      {level.label}
                    </p>
                    {isCurrent && currentTitle && (
                      <p className="text-sm text-lime-700">{currentTitle}</p>
                    )}
                  </div>

                  {/* Labels */}
                  {isCurrent && (
                    <Badge className="bg-lime-600 text-white">You are here</Badge>
                  )}
                  {isTarget && !isCurrent && (
                    <Badge variant="outline" className="border-blue-400 text-blue-600">
                      <Target className="h-3 w-3 mr-1" />
                      Target
                    </Badge>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Progress indicator */}
          {targetIndex > currentIndex && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100"
            >
              <div className="flex items-start gap-3">
                <Zap className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-blue-900">
                    {targetIndex - currentIndex} level{targetIndex - currentIndex > 1 ? 's' : ''} to your target
                  </p>
                  <p className="text-sm text-blue-700 mt-1">
                    Based on typical career progression, this usually takes {(targetIndex - currentIndex) * 3}-{(targetIndex - currentIndex) * 5} years
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

// AI Pathway Card Component
const PathwayCard = ({ pathway, index }) => {
  const [expanded, setExpanded] = useState(false);
  
  const likelihoodColors = {
    high: { bg: 'bg-green-100', text: 'text-green-800', label: 'High Fit' },
    medium: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Medium Fit' },
    low: { bg: 'bg-slate-100', text: 'text-slate-600', label: 'Stretch' }
  };
  
  const colors = likelihoodColors[pathway.likelihood] || likelihoodColors.medium;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.15 }}
    >
      <Card className="overflow-hidden hover:shadow-md transition-shadow cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`${colors.bg} ${colors.text}`}>
                  {colors.label}
                </Badge>
                <span className="text-xs text-slate-400">Pathway {index + 1}</span>
              </div>
              <h3 className="font-semibold text-lg text-slate-900 mb-1">{pathway.title}</h3>
              <p className="text-sm text-slate-600">{pathway.description}</p>
            </div>
            <ChevronRight className={`h-5 w-5 text-slate-400 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </div>

          <AnimatePresence>
            {expanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="mt-4 pt-4 border-t border-slate-100 space-y-4">
                  {/* Example Roles */}
                  {pathway.example_roles?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                        Example Roles
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {pathway.example_roles.map((role, idx) => (
                          <Badge key={idx} variant="outline" className="bg-white">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Skills to Develop */}
                  {pathway.skills_to_develop?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
                        Skills to Develop
                      </p>
                      <ul className="space-y-1">
                        {pathway.skills_to_develop.map((skill, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                            <GraduationCap className="h-3.5 w-3.5 text-lime-600" />
                            {skill}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Timeline */}
                  {pathway.typical_timeline && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Clock className="h-4 w-4 text-slate-400" />
                      <span>Typical timeline: {pathway.typical_timeline}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// AI Pathways Section
const AIPathways = ({ pathways, isLoading, onRefresh, isRefreshing, persona }) => {
  const personaMessages = {
    new_grad: {
      title: "Getting Started Pathways",
      subtitle: "Entry points into life sciences for new graduates"
    },
    career_changer: {
      title: "Transition Pathways",
      subtitle: "Leveraging your experience to break into pharma/biotech"
    },
    breaking_in: {
      title: "Growth Pathways",
      subtitle: "Building on your early pharma experience"
    },
    pharma_veteran: {
      title: "AI-Powered Career Pathways",
      subtitle: "Personalized recommendations based on your background"
    }
  };

  const messages = personaMessages[persona] || personaMessages.pharma_veteran;

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-lime-600 mx-auto mb-4" />
            <p className="text-slate-600">Analyzing your career potential...</p>
            <p className="text-sm text-slate-400 mt-1">This may take a moment</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!pathways || pathways.length === 0) {
    return (
      <Card className="border-dashed border-slate-300 bg-slate-50/50">
        <CardContent className="py-8 text-center">
          <Lightbulb className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 mb-2">No pathway analysis available</p>
          <p className="text-sm text-slate-400">Complete your profile and upload a resume for AI-powered career insights</p>
          <Button 
            variant="outline" 
            size="sm" 
            className="mt-4" 
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            {isRefreshing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Generate Pathways
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-lime-600" />
            {messages.title}
          </h2>
          <p className="text-sm text-slate-500">{messages.subtitle}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          disabled={isRefreshing}
          className="gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="grid gap-4">
        {pathways.map((pathway, idx) => (
          <PathwayCard key={idx} pathway={pathway} index={idx} />
        ))}
      </div>
    </div>
  );
};

// Help Modal
const CareerInsightsHelpModal = ({ isOpen, onClose, onNavigateToHelp }) => {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HelpCircle className="h-5 w-5 text-lime-600" />
            Career Insights
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">What is this page?</h4>
            <p className="text-sm text-slate-600">
              Career Insights visualizes your professional journey and projects possible future paths based on your experience, skills, and goals.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-1">How is this generated?</h4>
            <p className="text-sm text-slate-600">
              We analyze your uploaded resume and profile preferences using AI to create personalized career pathway recommendations.
            </p>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Tips</h4>
            <ul className="space-y-2">
              {[
                'Upload your latest resume for the most accurate analysis',
                'Click on pathways to see required skills',
                'Use "Refresh" to get new pathway suggestions',
                'Your trajectory updates as you update your target role'
              ].map((tip, idx) => (
                <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-lime-500 mt-0.5 flex-shrink-0" />
                  {tip}
                </li>
              ))}
            </ul>
          </div>
          <div className="pt-3 border-t border-slate-100">
            <button
              onClick={onNavigateToHelp}
              className="text-sm text-lime-600 hover:text-lime-700 font-medium flex items-center gap-1"
            >
              <BookOpen className="h-4 w-4" />
              View full Help Center
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
export default function CareerInsightsPage() {
  const { authAxios, profile, preferences, user } = useAuth();
  const navigate = useNavigate();
  
  const [insights, setInsights] = useState(null);
  const [pathways, setPathways] = useState([]);
  const [persona, setPersona] = useState('unknown');
  const [isLoadingInsights, setIsLoadingInsights] = useState(true);
  const [isLoadingPathways, setIsLoadingPathways] = useState(true);
  const [isRefreshingPathways, setIsRefreshingPathways] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // Load resume insights
  useEffect(() => {
    const loadInsights = async () => {
      try {
        const response = await authAxios.get('/candidates/resumes/insights');
        setInsights(response.data);
      } catch (error) {
        console.error('Failed to load resume insights:', error);
      } finally {
        setIsLoadingInsights(false);
      }
    };
    loadInsights();
  }, [authAxios]);

  // Load AI pathways (includes persona)
  useEffect(() => {
    const loadPathways = async () => {
      try {
        const response = await authAxios.get('/candidates/career-pathways');
        setPathways(response.data.pathways || []);
        setPersona(response.data.persona || 'unknown');
      } catch (error) {
        console.error('Failed to load career pathways:', error);
      } finally {
        setIsLoadingPathways(false);
      }
    };
    loadPathways();
  }, [authAxios]);

  // Analyze resume directly from Career Insights (fallback if auto-parse didn't complete)
  const [isAnalyzingResume, setIsAnalyzingResume] = useState(false);
  
  const handleAnalyzeResume = async () => {
    setIsAnalyzingResume(true);
    try {
      // First, get the user's resumes to find one to parse
      const resumesResponse = await authAxios.get('/candidates/resumes');
      const resumes = resumesResponse.data.resumes || [];
      
      if (resumes.length === 0) {
        toast.error('No resume found. Please upload a resume first.');
        return;
      }
      
      // Find the primary resume or the first one
      const resumeToParse = resumes.find(r => r.is_primary) || resumes[0];
      
      // Trigger parsing
      await authAxios.post(`/candidates/resumes/${resumeToParse.id}/parse`);
      toast.success('Resume analyzed successfully!');
      
      // Reload insights to show new data
      const insightsResponse = await authAxios.get('/candidates/resumes/insights');
      setInsights(insightsResponse.data);
      
      // Also reload pathways since they may now have better data
      const pathwaysResponse = await authAxios.get('/candidates/career-pathways');
      setPathways(pathwaysResponse.data.pathways || []);
      setPersona(pathwaysResponse.data.persona || persona);
      
    } catch (error) {
      console.error('Failed to analyze resume:', error);
      toast.error('Failed to analyze resume. Please try again.');
    } finally {
      setIsAnalyzingResume(false);
    }
  };

  // Refresh pathways
  const handleRefreshPathways = async () => {
    setIsRefreshingPathways(true);
    try {
      const response = await authAxios.post('/candidates/career-pathways/generate');
      setPathways(response.data.pathways || []);
      setPersona(response.data.persona || persona);
      toast.success('Career pathways updated!');
    } catch (error) {
      console.error('Failed to refresh pathways:', error);
      toast.error('Failed to refresh pathways');
    } finally {
      setIsRefreshingPathways(false);
    }
  };

  // Determine what to show based on persona
  const isNewGrad = persona === 'new_grad';
  const isCareerChanger = persona === 'career_changer' || persona === 'breaking_in';
  const hasResumeData = insights?.has_insights && insights?.company_history?.length > 0;
  const hasResumeUploaded = insights?.has_resume === true;
  const needsResumeAnalysis = hasResumeUploaded && !insights?.has_insights;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft className="h-4 w-4" />
              <span className="text-sm font-medium">Back to Dashboard</span>
            </Link>
            <Link to="/" className="text-xl font-heading text-evergreen-950">
              <img src="/varyynt-logo.svg" alt="Varyynt" className="h-10" />
            </Link>
            <div className="flex items-center gap-2">
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-slate-500 hover:text-lime-600"
                onClick={() => setShowHelp(true)}
                data-testid="career-insights-help-btn"
              >
                <HelpCircle className="h-4 w-4" />
              </Button>
              <NotificationBell />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading text-slate-900 mb-2">Career Insights</h1>
          <p className="text-slate-600">
            Visualize your journey and explore where your career could go next
          </p>
        </motion.div>

        {/* Loading state */}
        {isLoadingInsights || isLoadingPathways ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-lime-600" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Persona Banner */}
            {persona && persona !== 'unknown' && (
              <PersonaBanner persona={persona} />
            )}

            {/* Career Timeline - Different for each persona */}
            {isNewGrad && !hasResumeData ? (
              <NewGradStartingPoint 
                profile={profile} 
                hasResumeUploaded={hasResumeUploaded}
                needsResumeAnalysis={needsResumeAnalysis}
                onAnalyzeResume={handleAnalyzeResume}
                isAnalyzing={isAnalyzingResume}
              />
            ) : isCareerChanger && hasResumeData ? (
              <CareerChangerBridge insights={insights} profile={profile} />
            ) : (
              <CareerTimeline 
                insights={insights} 
                profile={profile}
                onAnalyzeResume={handleAnalyzeResume}
                isAnalyzing={isAnalyzingResume}
                hasResumeUploaded={hasResumeUploaded}
              />
            )}

            {/* Two column layout for Trajectory and Pathways */}
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Trajectory Map */}
              <TrajectoryMap insights={insights} preferences={preferences} profile={profile} />

              {/* AI Pathways */}
              <div>
                <AIPathways 
                  pathways={pathways} 
                  isLoading={isLoadingPathways}
                  onRefresh={handleRefreshPathways}
                  isRefreshing={isRefreshingPathways}
                  persona={persona}
                />
              </div>
            </div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="bg-gradient-to-r from-evergreen-950 to-evergreen-900 rounded-2xl p-8 text-white"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h2 className="text-xl font-heading mb-2">
                    {isNewGrad ? "Ready to start your journey?" : "Ready to take the next step?"}
                  </h2>
                  <p className="text-white/80">
                    {isNewGrad 
                      ? "Browse entry-level opportunities in life sciences"
                      : isCareerChanger 
                        ? "Find roles that value your unique background"
                        : "Browse job matches that align with your career trajectory"
                    }
                  </p>
                </div>
                <Link to="/matches">
                  <Button className="bg-white text-evergreen-950 hover:bg-lime-100 gap-2">
                    View Job Matches
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        )}
      </main>

      {/* Help Modal */}
      <CareerInsightsHelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
        onNavigateToHelp={() => {
          setShowHelp(false);
          navigate('/help');
        }}
      />
    </div>
  );
}
